import { ApolloError } from '@apollo/client';
import { type Id, toast } from 'react-toastify';
import { Status } from '../../graphql';
import type { NotificationCenterItem } from 'react-toastify/addons/use-notification-center';
import { v1 as uuid } from 'uuid';

// Helper functions to handle toast creation and updates
export const createLoadingToast = (message: string, toastId?: string) => {
  return toast(message, {
    type: toast.TYPE.INFO,
    isLoading: true,
    ...(toastId && { toastId }),
  });
};

export const createSuccessToast = (message: string, autoClose?: number) => {
  return toast(message, {
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    ...(autoClose && { autoClose }),
  });
};

export const createErrorToast = (message: string, autoClose?: number) => {
  return toast(message, {
    type: toast.TYPE.ERROR,
    isLoading: false,
    ...(autoClose && { autoClose }),
  });
};

export const updateToastSuccess = (
  toastId: string | number,
  message: string,
) => {
  toast.update(toastId, {
    render: message,
    type: toast.TYPE.SUCCESS,
    isLoading: false,
  });
};

export const updateToastError = (toastId: string | number, message: string) => {
  toast.update(toastId, {
    render: message,
    type: toast.TYPE.ERROR,
    isLoading: false,
  });
};

export const createWarningToast = (message: string, autoClose?: number) => {
  return toast(message, {
    type: toast.TYPE.WARNING,
    isLoading: false,
    ...(autoClose && { autoClose }),
  });
};

export const createWarningNotification = (
  add: (item: Partial<NotificationCenterItem<unknown>>) => Id | null,
  message: string,
) => {
  add({ id: uuid(), content: message, type: 'warning' });
};

export const handleApolloError = (
  error: unknown,
  autoClose: number | false = false,
) => {
  if (error instanceof ApolloError) {
    const messages = error.graphQLErrors.map((err) => err.message);
    for (const message of messages) {
      toast.error(message, {
        isLoading: false,
        autoClose,
      });
    }
  }
};

export const handleToastOfSyncData = (
  integrationName: string,
  type: 'orders' | 'vessels',
  response: {
    status: string;
    errorMessage?: string | null;
  },
  toastId?: Id,
) => {
  // Handle sync order status
  if (response.status === Status.ok) {
    if (toastId) {
      updateToastSuccess(
        toastId,
        `${integrationName} ${type} processing completed`,
      );
    } else {
      createSuccessToast(`${integrationName} ${type} processing completed`);
    }
  } else if (response.status === Status.fail && response.errorMessage) {
    if (toastId) {
      updateToastError(
        toastId,
        `Error from processing ${integrationName} ${type}: ${response.errorMessage}`,
      );
    } else {
      createErrorToast(
        `Error from processing ${integrationName} ${type}: ${response.errorMessage}`,
      );
    }
  }
};

export const handleToastOfScenarioUpdate = (
  integrationName: string,
  response: {
    status: string;
    warnings: string[];
    generatedTCLength: number;
  },
  add: (item: Partial<NotificationCenterItem<unknown>>) => Id | null,
  toastId?: Id,
) => {
  if (response.status === Status.ok) {
    if (toastId) {
      updateToastSuccess(toastId, `${integrationName} scenario updated`);
    } else {
      createSuccessToast(`${integrationName} scenario updated`);
    }

    if (response.warnings.length === 0) {
      if (toastId) {
        updateToastSuccess(
          toastId,
          `${integrationName} scenario updated with ${response.generatedTCLength} assignments`,
        );
      } else {
        createSuccessToast(
          `${integrationName} scenario updated with ${response.generatedTCLength} assignments`,
        );
      }
    } else {
      createWarningToast(`${integrationName} scenario updated with warnings:`);

      response.warnings.forEach((warning: string) => {
        createWarningNotification(add, warning);
      });
    }
  }
};
